import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Dialog, DialogContent, DialogActions, TableCell, TableBody, TableContainer, TableRow, TableHead, Table, Grid, TextField, Typography, IconButton, Autocomplete, Paper, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { getAllSeasonsAction, updateFarmerCommitmentAction, getAllDealersAction, getAllCropsAction, getAllVarietiesAction, getAllFarmersAction, getAllVillagesAction, getAllDistrictsAction, getAllMandalsAction, farmerCommitmentAction, clearUpdateFarmerCommitmentAction, clearfarmerCommitmentAction, getFarmerCommitmentByIdAction, clearGetFarmerCommitmentByIdAction, createDealerAction, clearCreateDealerAction, getAllFarmerCommitmentsAction } from "../../../store/actions";

import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// Search-or-create input for reference locations (district / mandal / village)
const RefLocationInput = ({ label, value, selectedId, suggestions, onTextChange, onSelect, disabled, error, helperText }) => {
    const [focused, setFocused] = React.useState(false);

    // Normalize: lowercase, remove all spaces
    const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
    const filtered = React.useMemo(() => {
        if (!value?.trim()) return [];
        const inputNorm = normalize(value);
        return suggestions.filter((s) =>
            normalize(s.label).includes(inputNorm)
        );
    }, [value, suggestions]);

    // Auto-select ID if value matches a suggestion exactly
    React.useEffect(() => {
        if (value && suggestions.length > 0) {
            const match = suggestions.find(s => s.label.toLowerCase() === value.toLowerCase().trim());
            if (match && selectedId !== match.id) {
                onSelect(match);
            }
        }
    }, [value, suggestions, selectedId, onSelect]);

    const showSuggestions = focused && value?.trim().length > 0 && filtered.length > 0;
    const isNew = value?.trim().length > 0 && !selectedId;

    let displayHelper = '';
    let helperColor;
    if (error && helperText) {
        displayHelper = helperText;
        helperColor = 'error.main';
    } else if (selectedId) {
        displayHelper = `✓ Existing ${label.toLowerCase()} selected`;
        helperColor = 'success.main';
    } else if (isNew) {
        displayHelper = `New ${label.toLowerCase()} will be created`;
        helperColor = 'info.main';
    }

    return (
        <Box sx={{ position: 'relative' }}>
            <TextField
                fullWidth
                size="small"
                label={label}
                value={value}
                onChange={(e) => {
                    const text = e.target.value;
                    onTextChange(text);
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 200)}
                disabled={disabled}
                error={error}
                helperText={displayHelper}
                placeholder={`Search or create ${label.toLowerCase()}`}
                FormHelperTextProps={{ sx: { color: helperColor } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, backgroundColor: '#FAFAFA' } }}
            />
            {showSuggestions && (
                <Paper
                    elevation={4}
                    sx={{ position: 'absolute', zIndex: 1300, width: '100%', maxHeight: 180, overflow: 'auto', mt: 0.5, borderRadius: 1 }}
                >
                    {filtered.map((item) => (
                        <Box
                            key={item.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { onSelect(item); setFocused(false); }}
                            sx={{ py: 0.75, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F3F6F9' } }}
                        >
                            <Typography variant="body2">{item.label}</Typography>
                        </Box>
                    ))}
                </Paper>
            )}
        </Box>
    );
};

function FarmerCommitmentDialogs({ open, onClose, selectedCommitmentId, farmerId }) {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Get logged-in user ID from localStorage
    const loggedInUserId = React.useMemo(() => {
        try {
            const userDetails = JSON.parse(localStorage.getItem('userDetails'));
            return userDetails?.id || null;
        } catch {
            return null;
        }
    }, []);

    console.log("farmerId", farmerId);

    const { successData: villages } = useSelector(state => state.getAllVillagesReducer);
    const villageOptions = villages?.data?.villages || villages?.villages || [];
    // Districts and Mandals
    const { successData: districts } = useSelector(state => state.getAllDistrictsReducer || {});
    const { successData: mandals } = useSelector(state => state.getAllMandalsReducer || {});
    const districtOptions = districts?.data?.districts || districts?.districts || [];
    const mandalOptions = mandals?.data?.mandals || mandals?.mandals || [];


    const { successData: createFormerCommitmentData, error: createFormerCommitmentError, errorInfo: createFormerCommitmentErrorInfo } = useSelector(state => state.farmerCommitmentReducer || {});
    const { successData: updateFormerCommitmentData, error: updateFormerCommitmentError, errorInfo: updateFormerCommitmentErrorInfo } = useSelector(state => state.updateFarmerCommitmentReducer);
    const { successData: commitmentByIdData } =
        useSelector((state) => state.getFarmerCommitmentByIdReducer);

    const { successData: dealers } = useSelector(state => state.getAllDealersReducer);

    const { successData: crops } = useSelector(state => state.getAllCropsReducer);
    const { successData: varieties } = useSelector(state => state.getAllVarietiesReducer);
    const { successData: farmers } = useSelector(state => state.getAllFarmersReducer);
    const { successData: seasons } = useSelector(state => state.getAllSeasonsReducer);

    // Options from store
    const dealerOptions = React.useMemo(() => (
        dealers?.data?.dealers || dealers?.dealers || []
    ), [dealers]);

    const cropOptions = crops?.data?.crops || crops?.crops || [];
    const varietyOptions = varieties?.data?.varieties || varieties?.varieties || [];
    const seasonOptions = seasons?.data?.seasons || seasons?.seasons || [];
    const farmerOptions = farmers?.data?.farmers || farmers?.farmers || [];

    // Normalize selectedCommitmentId
    const commitmentId = Number(selectedCommitmentId);
    const isEdit = Number.isFinite(commitmentId) && commitmentId > 0;


    const formik = useFormik({
        initialValues: {
            seasonId: '',
            farmerId: farmerId ? String(farmerId) : '',
            cropId: '',
            varietyId: '',
            expectedQty: '',
            expectedAcres: '',
            purchaseQty: '',
            dealerDistrictId: '',
            dealers: [{ id: Date.now(), dealerId: '' }],
            references: [],
            newReferenceName: '',
            newReferenceMobile: '',
            newReferenceDistrictName: '',
            newReferenceDistrictId: '',
            newReferenceMandalName: '',
            newReferenceMandalId: '',
            newReferenceVillageName: '',
            newReferenceVillageId: '',
            status: 'Pending',
        },
        validate: (values) => {
            const errs = {};

            // In edit mode, only validate the editable fields
            if (isEdit) {
                // No required validation for edit-only fields; they are optional
                return errs;
            }

            if (!values.seasonId) errs.seasonId = 'Season is required';
            if (!values.farmerId) errs.farmerId = 'Farmer is required';
            if (!values.cropId) errs.cropId = 'Crop is required';
            if (!values.varietyId) errs.varietyId = 'Variety is required';

            const qty = Number(values.expectedQty);
            if (!values.expectedQty || Number.isNaN(qty) || qty <= 0) errs.expectedQty = 'Expected Qty must be greater than 0';

            const acres = Number(values.expectedAcres);
            if (!values.expectedAcres || Number.isNaN(acres) || acres <= 0) {
                errs.expectedAcres = 'Expected Acres must be greater than 0';
            } else if (values.farmerId) {
                const selectedFarmer = farmerOptions.find(
                    (f) => String(f.farmerId) === String(values.farmerId)
                );
                const totalAcres = Number(
                    selectedFarmer?.totalAcres ?? selectedFarmer?.total_acres
                );
                if (
                    selectedFarmer &&
                    !Number.isNaN(totalAcres) &&
                    totalAcres > 0 &&
                    acres > totalAcres
                ) {
                    errs.expectedAcres = `Expected Acres (${acres}) cannot exceed farmer's total registered acres (${totalAcres})`;
                }
            }

            // Dealer validation
            const selectedDealers = (values.dealers || []).map((d) => d.dealerId).filter(Boolean);
            if (selectedDealers.length === 0) errs.dealers = 'Select at least one dealer';
            else if (new Set(selectedDealers).size !== selectedDealers.length) errs.dealers = 'Duplicate dealers not allowed';

            // Reference validation: only validate rows with any data
            const refErrs = [];
            (values.references || []).forEach((r, idx) => {
                if (!r.name && !r.mobile && !r.districtName && !r.mandalName && !r.villageName) return;
                const rErr = {};
                if (!r.name) rErr.name = 'Name is required';
                else if (!/^[A-Za-z ]+$/.test(r.name)) rErr.name = 'Name must contain only letters';
                if (!r.mobile) rErr.mobile = 'Mobile is required';
                else if (!/^\d+$/.test(r.mobile)) rErr.mobile = 'Mobile must contain only numbers';
                else if (r.mobile.length !== 10) rErr.mobile = 'Mobile must be exactly 10 digits';
                if (!r.districtName) rErr.district = 'District is required';
                if (!r.mandalName) rErr.mandal = 'Mandal is required';
                if (!r.villageName) rErr.village = 'Village is required';
                if (Object.keys(rErr).length) refErrs[idx] = rErr;
            });
            if (refErrs.length) errs.references = refErrs;
            return errs;
        },

        onSubmit: async (values) => {
            const isEditMode = isEdit;
            if (isEditMode) {
                // Send all fields in FormData for update
                const fd = new FormData();
                fd.append('season_id', Number(values.seasonId));
                fd.append('farmer_id', Number(values.farmerId));
                fd.append('crop_id', Number(values.cropId));
                fd.append('variety_id', Number(values.varietyId));
                fd.append('expected_qty', values.expectedQty ? String(values.expectedQty) : "0");
                fd.append('expected_acre', values.expectedAcres ? String(values.expectedAcres) : "0");
                (values.dealers || []).forEach((d, idx) => {
                    if (d.dealerId) fd.append(`expected_dealer_id[${idx}]`, Number(d.dealerId));
                });
                fd.append('purchase_qty', values.purchaseQty || '');
                fd.append('purchased_dealer_id', values.purchasedDealerId || '');
                fd.append('variety_name', values.varietyName || '');
                fd.append('invoice_no', values.invoiceNo || '');
                if (values.inv_photo) fd.append('inv_photo', values.inv_photo);
                // Status field
                fd.append('status', values.status || 'Pending');
                // References
                if (Array.isArray(values.references)) {
                    values.references.filter(r => r.name && r.mobile && r.districtId && (r.mandalId || r.mandalName) && (r.villageId || r.villageName)).forEach((r, idx) => {
                        fd.append(`refer[${idx}][farmer_id]`, r.referenceFarmerId ? Number(r.referenceFarmerId) : (r.farmerId ? Number(r.farmerId) : Number(values.farmerId)));
                        fd.append(`refer[${idx}][name]`, r.name);
                        fd.append(`refer[${idx}][mobile]`, r.mobile);
                        fd.append(`refer[${idx}][district_id]`, Number(r.districtId));
                        fd.append(`refer[${idx}][mandal_id]`, r.mandalId ? Number(r.mandalId) : r.mandalName);
                        fd.append(`refer[${idx}][village_id]`, r.villageId ? Number(r.villageId) : r.villageName);
                    });
                }
                fd.endPoint = `farmer-commitment/${commitmentId}`;
                dispatch(updateFarmerCommitmentAction(fd));
                return;
            }
            // CREATE MODE: send all fields with correct types and refer key
            const payload = {
                season_id: Number(values.seasonId),
                farmer_id: Number(values.farmerId),
                crop_id: Number(values.cropId),
                variety_id: Number(values.varietyId),
                expected_qty: values.expectedQty ? String(values.expectedQty) : "0",
                expected_acre: values.expectedAcres ? String(values.expectedAcres) : "0",
                expected_dealer_id: (values.dealers || []).map(d => Number(d.dealerId)).filter(Boolean),
                refer: Array.isArray(values.references)
                    ? values.references.filter(r => r.name && r.mobile && r.districtId && (r.mandalId || r.mandalName) && (r.villageId || r.villageName)).map(r => ({
                        farmer_id: r.referenceFarmerId ? Number(r.referenceFarmerId) : (r.farmerId ? Number(r.farmerId) : Number(values.farmerId)),
                        name: r.name,
                        mobile: r.mobile,
                        district_id: Number(r.districtId),
                        mandal_id: r.mandalId ? Number(r.mandalId) : r.mandalName,
                        village_id: r.villageId ? Number(r.villageId) : r.villageName
                    }))
                    : []
            };
            dispatch(farmerCommitmentAction(payload));
            setOpenRefDialog(false);
        }
    });

    const prevOpenRef = React.useRef(false);
    const { setValues } = formik;

    const [openRefDialog, setOpenRefDialog] = useState(false);
    const [refSubmitError, setRefSubmitError] = useState('');
    // Dealer creation dialog state
    const [openDealerDialog, setOpenDealerDialog] = useState(false);
    const [dealerForm, setDealerForm] = useState({
        fullName: '',
        districtId: '',
        mandalId: '',
        villageId: ''
    });


    const filteredVarietyOptions = varietyOptions.filter(
        (v) => String(v.cropId) === String(formik.values.cropId) && (v.status ? v.status === 'ACTIVE' : true)
    );

    // Preview URL for the Invoice Photo field: a freshly picked file wins over
    // the previously uploaded photo returned by the API.
    const invoicePhotoPreviewUrl = React.useMemo(() => {
        if (formik.values.inv_photo) return URL.createObjectURL(formik.values.inv_photo);
        return formik.values.existingInvPhoto || '';
    }, [formik.values.inv_photo, formik.values.existingInvPhoto]);

    useEffect(() => {
        if (!formik.values.inv_photo) return;
        return () => URL.revokeObjectURL(invoicePhotoPreviewUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoicePhotoPreviewUrl]);

    const [invoicePhotoFailed, setInvoicePhotoFailed] = useState(false);
    useEffect(() => {
        setInvoicePhotoFailed(false);
    }, [invoicePhotoPreviewUrl]);

    const handleOpenDealerDialog = () => {
        setOpenDealerDialog(true);
    };

    const handleAddDealerField = () => {
        setValues((prev) => ({
            ...prev,
            dealers: [...(prev.dealers || []), { id: Date.now() + Math.random(), dealerId: '', districtId: '' }],
        }));
    };

    // Dealer dialog form handlers
    const handleDealerDialogClose = () => {
        setOpenDealerDialog(false);
        setDealerForm({ fullName: '', districtId: '', mandalId: '', villageId: '' });
        dispatch(clearCreateDealerAction());
    };

    const handleDealerFormChange = (field, value) => {
        setDealerForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleDealerCreate = () => {
        if (
            dealerForm.fullName &&
            dealerForm.districtId &&
            (dealerForm.mandalId || dealerForm.mandalName) &&
            (dealerForm.villageId || dealerForm.villageName)
        ) {
            dispatch(createDealerAction({
                name: dealerForm.fullName,
                districtId: Number(dealerForm.districtId),
                mandalId: dealerForm.mandalId
                    ? Number(dealerForm.mandalId)
                    : dealerForm.mandalName,
                villageId: dealerForm.villageId
                    ? Number(dealerForm.villageId)
                    : dealerForm.villageName,
                incharge_user_id: loggedInUserId
            }));
        }
    };

    const handleDealerFieldChange = (idx, value) => {
        const arr = [...formik.values.dealers];
        arr[idx] = { ...arr[idx], dealerId: value || '' };
        formik.setFieldValue('dealers', arr);
    };
    const handleDealerDistrictChange = (idx, value) => {
        const arr = [...formik.values.dealers];
        arr[idx] = { ...arr[idx], districtId: value || '', dealerId: '' };
        formik.setFieldValue('dealers', arr);
    };

    // ─── CHANGED: handleNext now directly submits in edit mode ───
    const handleNext = async () => {
        // In edit mode: directly submit without showing reference dialog
        if (isEdit) {
            formik.handleSubmit();
            return;
        }

        // Create mode: validate and open reference dialog
        const errs = await formik.validateForm();
        const keysToCheck = ['seasonId', 'farmerId', 'cropId', 'varietyId', 'expectedQty', 'expectedAcres', 'dealers'];
        const hasErrors = keysToCheck.some((k) => {
            if (k === 'dealers') return Boolean(errs.dealers);
            return Boolean(errs[k]);
        });
        if (hasErrors) {
            formik.setTouched({
                seasonId: true,
                farmerId: true,
                cropId: true,
                varietyId: true,
                expectedQty: true,
                expectedAcres: true,
            });
            return;
        }
        if (onClose) onClose();
        setOpenRefDialog(true);
    };

    const handleRefClose = () => setOpenRefDialog(false);

    useEffect(() => {
        if (open && !isEdit && !prevOpenRef.current) {
            formik.resetForm();
            dispatch(clearGetFarmerCommitmentByIdAction());
        }
        prevOpenRef.current = open;
        // eslint-disable-next-line
    }, [open, isEdit, dealerOptions, dispatch]);

    useEffect(() => {
        if (open && isEdit) {
            dispatch(getFarmerCommitmentByIdAction({
                endPoint: `farmer-commitment/${commitmentId}`,
            }));
        }
    }, [open, isEdit, commitmentId, dispatch]);


    useEffect(() => {
        if (!open) return;

        if (isEdit && commitmentByIdData?.data) {
            const c = commitmentByIdData.data;
            setValues({
                seasonId: c.season_id ? String(c.season_id) : '',
                farmerId: c.farmer_id ? String(c.farmer_id) : '',
                cropId: c.crop_id ? String(c.crop_id) : '',
                varietyId: c.variety_id ? String(c.variety_id) : '',
                expectedQty: c.expected_qty ? String(c.expected_qty) : '',
                expectedAcres: c.expected_acre ? String(c.expected_acre) : '',
                purchaseQty: c.purchase_qty ? String(c.purchase_qty) : '',
                purchasedDealerId: c.purchased_dealer_id ? String(c.purchased_dealer_id) : '',
                varietyName: c.variety_name
                    || c.variety?.name
                    || varietyOptions.find((v) => String(v.varietyId) === String(c.variety_id))?.name
                    || (c.variety_id ? String(c.variety_id) : ''),
                invoiceNo: c.invoice_no || c.invoiceNo || '',
                existingInvPhoto: c.inv_photo || c.invPhoto || '',
                dealers: (c.expected_dealer_id || []).length
                    ? c.expected_dealer_id.map((id) => ({
                        id: Date.now() + Math.random(),
                        dealerId: typeof id === 'object'
                            ? String(id?.dealerId ?? id?.id ?? '')
                            : String(id),
                    })).filter((d) => d.dealerId !== '')
                    : [{ id: Date.now(), dealerId: '' }],
                references: (c.farmer?.references || []).length
                    ? c.farmer.references.map((r) => ({
                        id: Date.now() + Math.random(),
                        referenceId: r.id || null,
                        name: r.name || '',
                        mobile: r.mobile || '',
                        district: r.district_id ? String(r.district_id) : '',
                        districtName: districtOptions.find(d => String(d.districtId) === String(r.district_id))?.name || '',
                        mandal: r.mandal_id ? String(r.mandal_id) : '',
                        mandalName: mandalOptions.find(m => String(m.mandalId) === String(r.mandal_id))?.name || '',
                        village: r.village_id ? String(r.village_id) : '',
                        villageName: villageOptions.find(v => String(v.villageId) === String(r.village_id))?.name || '',
                    }))
                    : [],
                newReferenceName: '',
                newReferenceMobile: '',
                newReferenceDistrictName: '',
                newReferenceDistrictId: '',
                newReferenceMandalName: '',
                newReferenceMandalId: '',
                newReferenceVillageName: '',
                newReferenceVillageId: '',
                status: c.status || 'Pending',
            });
        }
    }, [commitmentByIdData, isEdit, open, setValues]);

    // Auto-fill District based on the dealer already assigned to this commitment (edit mode)
    useEffect(() => {
        if (!open || !isEdit) return;
        if (formik.values.dealerDistrictId) return;
        const assignedDealerId = (formik.values.dealers || []).find((d) => d.dealerId)?.dealerId;
        if (!assignedDealerId || !dealerOptions.length) return;
        const dealerObj = dealerOptions.find((d) => String(d.dealerId) === String(assignedDealerId));
        if (dealerObj?.districtId) {
            formik.setFieldValue('dealerDistrictId', String(dealerObj.districtId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEdit, formik.values.dealers, dealerOptions]);

    useEffect(() => {
        if (open && !isEdit) {
            dispatch(clearGetFarmerCommitmentByIdAction());
        }
    }, [open, isEdit, dispatch]);

    // Autofill farmerId when prop changes and dialog is open and not in edit mode
    useEffect(() => {
        if (open && !isEdit && farmerId) {
            setValues((prev) => ({
                ...prev,
                farmerId: String(farmerId)
            }));
        }
    }, [open, isEdit, farmerId, setValues]);

    useEffect(() => {
        dispatch(getAllDealersAction());
        dispatch(getAllCropsAction());
        dispatch(getAllVarietiesAction());
        dispatch(getAllFarmersAction());
        dispatch(getAllSeasonsAction());
        dispatch(getAllVillagesAction());
        dispatch(getAllDistrictsAction());
        dispatch(getAllMandalsAction());
    }, [dispatch]);

    // Auto-fill reference district when only one district option is available
    useEffect(() => {
        if (districtOptions.length === 1 && !formik.values.newReferenceDistrictId) {
            const only = districtOptions[0];
            formik.setFieldValue('newReferenceDistrictName', only.name);
            formik.setFieldValue('newReferenceDistrictId', String(only.districtId));
        }
    }, [districtOptions]);

    useEffect(() => {
        if (createFormerCommitmentData && (createFormerCommitmentData.success || createFormerCommitmentData.data || createFormerCommitmentData.statusCode === 201 || createFormerCommitmentData.statusCode === 200)) {
            toast.success("Commitment created successfully");
            dispatch(clearfarmerCommitmentAction());
            dispatch(getAllFarmerCommitmentsAction());
            if (onClose) onClose();
        } else if (createFormerCommitmentData?.message && !createFormerCommitmentData?.success) {
            toast.error(createFormerCommitmentData.message);
            dispatch(clearfarmerCommitmentAction());
        }
        if (updateFormerCommitmentData && (updateFormerCommitmentData.success || updateFormerCommitmentData.data || updateFormerCommitmentData.statusCode === 200 || updateFormerCommitmentData.statusCode === 201)) {
            toast.success("Commitment updated successfully");
            dispatch(clearUpdateFarmerCommitmentAction());
            dispatch(getAllFarmerCommitmentsAction());
            if (onClose) onClose();
        } else if (updateFormerCommitmentData?.message && !updateFormerCommitmentData?.success) {
            toast.error(updateFormerCommitmentData.message);
            dispatch(clearUpdateFarmerCommitmentAction());
        }
    }, [createFormerCommitmentData, updateFormerCommitmentData, dispatch, onClose]);

    // Listen for dealer creation success
    const { successData: createDealerSuccess, error: createDealerError } = useSelector(state => state.createDealerReducer || {});
    // Callback to add dealer after creation
    const addDealerAfterCreate = React.useCallback((newDealerId) => {
        setValues((prev) => ({
            ...prev,
            dealers: [
                ...(prev.dealers || []),
                { id: Date.now() + Math.random(), dealerId: String(newDealerId) }
            ]
        }));
    }, [setValues]);

    useEffect(() => {
        if (createDealerSuccess) {
            toast.success("Dealer created successfully");
            const newDealerId = createDealerSuccess.data?.dealerId || createDealerSuccess.dealerId;
            if (newDealerId) {
                addDealerAfterCreate(newDealerId);
            }
            setOpenDealerDialog(false);
            setDealerForm({ fullName: '', districtId: '', mandalId: '', villageId: '' });
            dispatch(clearCreateDealerAction());
            dispatch(getAllDealersAction());
            // Fetch latest mandals and villages for reference dialog
            dispatch(getAllMandalsAction());
            dispatch(getAllVillagesAction());
        } else if (createDealerError) {
            toast.error("Failed to create dealer");
            dispatch(clearCreateDealerAction());
        }
    }, [createDealerSuccess, createDealerError, dispatch, addDealerAfterCreate]);

    useEffect(() => {
        if (createFormerCommitmentError) {
            toast.error(createFormerCommitmentErrorInfo);
            dispatch(clearfarmerCommitmentAction())
        }
        if (updateFormerCommitmentError) {
            toast.error(updateFormerCommitmentErrorInfo);
            dispatch(clearUpdateFarmerCommitmentAction())
        }
    }, [createFormerCommitmentError, createFormerCommitmentErrorInfo, updateFormerCommitmentError, updateFormerCommitmentErrorInfo, dispatch]);

    return (
        <>
            {/* Add / Edit Commitment Dialog */}
            <Dialog
                open={open}
                onClose={() => {
                    formik.resetForm();
                    dispatch(clearGetFarmerCommitmentByIdAction());
                    onClose();
                }}
                fullWidth
                maxWidth="md"
                slotProps={{ paper: { sx: { borderRadius: 3 } } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, pb: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Farmer Commitment
                    </Typography>
                    <IconButton
                        onClick={() => {
                            formik.resetForm();
                            dispatch(clearGetFarmerCommitmentByIdAction());
                            onClose();
                        }}
                        aria-label="close dialog"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <Grid container spacing={2}>
                        {/* Season */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                Season
                            </Typography>
                            <Autocomplete
                                options={seasonOptions}
                                value={seasonOptions.find((s) => String(s.seasonId) === String(formik.values.seasonId)) || null}
                                onChange={(_, newValue) => {
                                    if (!isEdit) formik.setFieldValue('seasonId', newValue ? String(newValue.seasonId) : '');
                                }}
                                getOptionLabel={(s) => (s ? `${s.name} (${s.year})` : '')}
                                isOptionEqualToValue={(option, value) => String(option.seasonId) === String(value.seasonId)}
                                clearOnEscape
                                fullWidth
                                // ─── DISABLED in edit mode ───
                                disabled={isEdit}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Select season" size="small" error={Boolean(formik.touched.seasonId && formik.errors.seasonId)} helperText={formik.touched.seasonId && formik.errors.seasonId} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) } }} />
                                )}
                            />
                        </Grid>
                        {/* Farmer */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                Farmer
                            </Typography>
                            <Autocomplete
                                options={farmerOptions}
                                value={farmerOptions.find((f) => String(f.farmerId) === String(formik.values.farmerId)) || null}
                                onChange={(_, newValue) => {
                                    if (!isEdit) formik.setFieldValue('farmerId', newValue ? String(newValue.farmerId) : '');
                                }}
                                getOptionLabel={(f) => {
                                    if (!f) return '';
                                    return f.mobile ? `${f.name} (${f.mobile})` : f.name;
                                }}
                                isOptionEqualToValue={(option, value) => String(option.farmerId) === String(value.farmerId)}
                                clearOnEscape
                                fullWidth
                                // ─── DISABLED in edit mode ───
                                disabled={isEdit}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Select farmer" size="small" error={Boolean(formik.touched.farmerId && formik.errors.farmerId)} helperText={formik.touched.farmerId && formik.errors.farmerId} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) } }} />
                                )}
                            />
                        </Grid>

                        {/* Crop */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                Crop
                            </Typography>
                            <Autocomplete
                                options={cropOptions.filter((c) => (c.status ? c.status === 'ACTIVE' : true))}
                                value={cropOptions.find((c) => String(c.cropId) === String(formik.values.cropId)) || null}
                                onChange={(_, newValue) => {
                                    if (!isEdit) {
                                        formik.setFieldValue('cropId', newValue ? String(newValue.cropId) : '');
                                        formik.setFieldValue('varietyId', '');
                                    }
                                }}
                                getOptionLabel={(c) => (c ? c.name : '')}
                                isOptionEqualToValue={(option, value) => String(option.cropId) === String(value.cropId)}
                                clearOnEscape
                                fullWidth
                                // ─── DISABLED in edit mode ───
                                disabled={isEdit}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Select crop" size="small" error={Boolean(formik.touched.cropId && formik.errors.cropId)} helperText={formik.touched.cropId && formik.errors.cropId} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) } }} />
                                )}
                            />
                        </Grid>
                        {/* Variety */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                Variety
                            </Typography>
                            <Autocomplete
                                options={filteredVarietyOptions}
                                value={filteredVarietyOptions.find((v) => String(v.varietyId) === String(formik.values.varietyId)) || null}
                                onChange={(_, newValue) => {
                                    if (!isEdit) formik.setFieldValue('varietyId', newValue ? String(newValue.varietyId) : '');
                                }}
                                getOptionLabel={(v) => (v ? v.name : '')}
                                isOptionEqualToValue={(option, value) => String(option.varietyId) === String(value.varietyId)}
                                clearOnEscape
                                // ─── DISABLED in edit mode ───
                                disabled={isEdit || !formik.values.cropId}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={formik.values.cropId ? 'Select variety' : 'Select crop first'} size="small" error={Boolean(formik.touched.varietyId && formik.errors.varietyId)} helperText={formik.touched.varietyId && formik.errors.varietyId} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) } }} />
                                )}
                            />
                        </Grid>

                        {/* Expected Qty */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                Expected Qty
                            </Typography>
                            <TextField
                                placeholder=""
                                type="number"
                                size="small"
                                name="expectedQty"
                                value={formik.values.expectedQty}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                fullWidth
                                // ─── DISABLED in edit mode ───
                                disabled={isEdit}
                                error={Boolean(formik.touched.expectedQty && formik.errors.expectedQty)}
                                helperText={formik.touched.expectedQty && formik.errors.expectedQty}
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) },
                                    '& input[type=number]': { MozAppearance: 'textfield', appearance: 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                }}
                                inputProps={{
                                    onWheel: e => e.target.blur(),
                                    onKeyDown: e => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                        }
                                    }
                                }}
                            />
                        </Grid>
                        {/* Expected Acres */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                Expected Acres
                            </Typography>
                            <TextField
                                placeholder=""
                                type="number"
                                size="small"
                                name="expectedAcres"
                                value={formik.values.expectedAcres}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                fullWidth
                                // ─── DISABLED in edit mode ───
                                disabled={isEdit}
                                error={Boolean(formik.touched.expectedAcres && formik.errors.expectedAcres)}
                                helperText={formik.touched.expectedAcres && formik.errors.expectedAcres}
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) },
                                    '& input[type=number]': { MozAppearance: 'textfield', appearance: 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                }}
                                inputProps={{
                                    onWheel: e => e.target.blur(),
                                    onKeyDown: e => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        {/* District - DISABLED in edit mode */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                District
                            </Typography>
                            <Autocomplete
                                fullWidth
                                options={districtOptions}
                                value={districtOptions.find((d) => String(d.districtId) === String(formik.values.dealerDistrictId)) || null}
                                onChange={(_, newValue) => {
                                    if (!isEdit) {
                                        setValues((prev) => ({
                                            ...prev,
                                            dealerDistrictId: newValue ? String(newValue.districtId) : '',
                                            dealers: (prev.dealers || []).map(d => ({ ...d, dealerId: '' })),
                                        }));
                                    }
                                }}
                                getOptionLabel={(d) => d ? d.name : ''}
                                isOptionEqualToValue={(option, value) => String(option.districtId) === String(value.districtId)}
                                clearOnEscape
                                // ─── DISABLED in edit mode ───
                                disabled={isEdit}
                                sx={{ minWidth: 120 }}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="District" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) } }} />
                                )}
                            />
                        </Grid>

                        {/* Dealer - DISABLED in edit mode */}
                        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                Dealer
                            </Typography>
                            {(formik.values.dealers || []).map((item, idx) => {
                                const dealerOptionsForRow = formik.values.dealerDistrictId
                                    ? dealerOptions.filter((dealer) => String(dealer.districtId) === String(formik.values.dealerDistrictId))
                                    : dealerOptions;
                                return (
                                    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', mb: idx === (formik.values.dealers || []).length - 1 ? 0 : 1.5 }}>
                                        <Autocomplete
                                            options={dealerOptionsForRow}
                                            value={dealerOptionsForRow.find((d) => String(d.dealerId) === String(item.dealerId)) || null}
                                            onChange={(_, newValue) => {
                                                if (!isEdit) handleDealerFieldChange(idx, newValue ? newValue.dealerId : '');
                                            }}
                                            getOptionLabel={(d) => (d ? d.name : '')}
                                            isOptionEqualToValue={(option, value) => String(option.dealerId) === String(value.dealerId)}
                                            clearOnEscape
                                            fullWidth
                                            // ─── DISABLED in edit mode ───
                                            disabled={isEdit}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder="Select dealer" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, ...(isEdit && { backgroundColor: '#F3F4F6' }) } }} />
                                            )}
                                        />
                                        {/* ─── Hide add-dealer-field icon in edit mode ─── */}
                                        {!isEdit && (
                                            <IconButton color="primary" onClick={handleAddDealerField} sx={{ ml: 1, bgcolor: '#EEF4FF', borderRadius: 2 }} aria-label="add dealer field">
                                                <AddIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })}
                            {formik.errors.dealers && (
                                <Typography variant="caption" sx={{ color: '#F75555', mt: 1 }}>
                                    {formik.errors.dealers}
                                </Typography>
                            )}
                            {/* ─── Hide "Add New Dealer" button in edit mode ─── */}
                            {!isEdit && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleOpenDealerDialog}
                                        sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Add New Dealer
                                    </Button>
                                </Box>
                            )}
                        </Grid>

                        {/* Purchase Qty - Only shown in edit mode */}
                        {isEdit && (
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                    Purchase Qty
                                </Typography>
                                <TextField
                                    placeholder=""
                                    type="number"
                                    size="small"
                                    name="purchaseQty"
                                    value={formik.values.purchaseQty}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: 2.5 },
                                        '& input[type=number]': { MozAppearance: 'textfield', appearance: 'textfield' },
                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                    }}
                                />
                            </Grid>
                        )}

                        {/* Purchase Dealer - Only shown in edit mode */}
                        {isEdit && (
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                    Purchase Dealer
                                </Typography>
                                <Autocomplete
                                    options={dealerOptions}
                                    value={dealerOptions.find((d) => String(d.dealerId) === String(formik.values.purchasedDealerId)) || null}
                                    onChange={(_, newValue) => formik.setFieldValue('purchasedDealerId', newValue ? String(newValue.dealerId) : '')}
                                    getOptionLabel={(d) => d ? d.name : ''}
                                    isOptionEqualToValue={(option, value) => String(option.dealerId) === String(value.dealerId)}
                                    clearOnEscape
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select dealer" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Variety Name - Only shown in edit mode */}
                        {isEdit && (
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                    Variety Name
                                </Typography>
                                <Autocomplete
                                    options={filteredVarietyOptions}
                                    value={filteredVarietyOptions.find((v) => v.name === formik.values.varietyName) || null}
                                    onChange={(_, newValue) => {
                                        formik.setFieldValue('varietyName', newValue ? newValue.name : '');
                                    }}
                                    getOptionLabel={(v) => (v ? v.name : '')}
                                    isOptionEqualToValue={(option, value) => option.name === value.name}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select variety name" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Invoice No - Only shown in edit mode */}
                        {isEdit && (
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                    Invoice No
                                </Typography>
                                <TextField
                                    placeholder=""
                                    size="small"
                                    name="invoiceNo"
                                    value={formik.values.invoiceNo}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    fullWidth
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                                />
                            </Grid>
                        )}
                        {/* Status Dropdown - Only in edit mode, before Invoice Photo */}
                        {isEdit && (
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                    Status
                                </Typography>
                                <TextField
                                    select
                                    name="status"
                                    value={formik.values.status || ''}
                                    onChange={e => formik.setFieldValue('status', e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                                    error={!formik.values.status}
                                    helperText={!formik.values.status ? 'Select status' : ''}
                                >
                                    <MenuItem value="" disabled hidden>Select status</MenuItem>
                                    <MenuItem value="Approved">Purchased</MenuItem>
                                    <MenuItem value="Pending">Commitment</MenuItem>
                                    <MenuItem value="Rejected">Rejected</MenuItem>
                                </TextField>
                            </Grid>
                        )}

                        {/* Invoice Photo - Only shown in edit mode */}
                        {isEdit && (
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280', fontWeight: 600 }}>
                                    Invoice Photo
                                </Typography>
                                {invoicePhotoPreviewUrl && invoicePhotoFailed ? (
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'error.main' }}>
                                        Photo unavailable (access denied by storage).{' '}
                                        <a href={invoicePhotoPreviewUrl} target="_blank" rel="noopener noreferrer">Open link</a>
                                    </Typography>
                                ) : invoicePhotoPreviewUrl ? (
                                    <Box
                                        component="a"
                                        href={invoicePhotoPreviewUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ display: 'block', mb: 1 }}
                                    >
                                        <Box
                                            component="img"
                                            src={invoicePhotoPreviewUrl}
                                            alt="Invoice"
                                            onError={() => setInvoicePhotoFailed(true)}
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                                border: '1px solid #E0E0E0',
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.disabled' }}>
                                        No invoice photo uploaded
                                    </Typography>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={e => formik.setFieldValue('inv_photo', e.target.files[0])}
                                    style={{ marginTop: 8 }}
                                />
                            </Grid>
                        )}

                        {/* Dealer Creation Dialog - only accessible in create mode */}
                        {!isEdit && (
                            <Dialog
                                open={openDealerDialog}
                                onClose={handleDealerDialogClose}
                                fullWidth
                                maxWidth="sm"
                                slotProps={{ paper: { sx: { borderRadius: 3 } } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, pb: 0 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        Create Dealer
                                    </Typography>
                                    <IconButton onClick={handleDealerDialogClose} aria-label="close dialog">
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                                <DialogContent sx={{ pt: 2, pb: 1 }}>
                                    <Grid container spacing={2}>
                                        <Grid item size={{ xs: 12, sm: 3 }}>
                                            <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>Dealer Name</Typography>
                                            <TextField size="small" fullWidth value={dealerForm.fullName} onChange={e => handleDealerFormChange('fullName', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} />
                                        </Grid>
                                        <Grid item size={{ xs: 12, sm: 3 }}>
                                            <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>District</Typography>
                                            <Autocomplete
                                                options={districtOptions}
                                                value={districtOptions.find((d) => String(d.districtId) === String(dealerForm.districtId)) || null}
                                                onChange={(_, newValue) => {
                                                    handleDealerFormChange('districtId', newValue ? String(newValue.districtId) : '');
                                                    handleDealerFormChange('districtName', newValue ? newValue.name : '');
                                                    handleDealerFormChange('mandalId', '');
                                                    handleDealerFormChange('mandalName', '');
                                                    handleDealerFormChange('villageId', '');
                                                    handleDealerFormChange('villageName', '');
                                                }}
                                                getOptionLabel={(d) => d ? d.name : ''}
                                                isOptionEqualToValue={(option, value) => String(option.districtId) === String(value.districtId)}
                                                renderInput={(params) => (
                                                    <TextField {...params} placeholder="Select district" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item size={{ xs: 12, sm: 3 }}>
                                            <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>Mandal</Typography>
                                            <RefLocationInput
                                                label="Mandal"
                                                value={mandalOptions.find((m) => String(m.mandalId) === String(dealerForm.mandalId))?.name || dealerForm.mandalName || ''}
                                                selectedId={dealerForm.mandalId}
                                                suggestions={
                                                    (dealerForm.districtId
                                                        ? mandalOptions.filter((m) => String(m.districtId) === String(dealerForm.districtId))
                                                        : mandalOptions
                                                    ).map(m => ({ id: String(m.mandalId), label: m.name }))
                                                }
                                                disabled={false}
                                                onTextChange={(text) => {
                                                    handleDealerFormChange('mandalName', text);
                                                    // Normalize: lowercase, remove all spaces
                                                    const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                                                    const inputNorm = normalize(text);
                                                    const mandalSuggs = dealerForm.districtId
                                                        ? mandalOptions.filter((m) => String(m.districtId) === String(dealerForm.districtId))
                                                        : mandalOptions;
                                                    const mandalMatch = mandalSuggs.find(m => normalize(m.name) === inputNorm);
                                                    handleDealerFormChange('mandalId', mandalMatch ? String(mandalMatch.mandalId) : '');
                                                    handleDealerFormChange('villageId', '');
                                                    handleDealerFormChange('villageName', '');
                                                }}
                                                onSelect={(item) => {
                                                    handleDealerFormChange('mandalId', item.id);
                                                    handleDealerFormChange('mandalName', item.label);
                                                    handleDealerFormChange('villageId', '');
                                                    handleDealerFormChange('villageName', '');
                                                }}
                                            />
                                        </Grid>
                                        <Grid item size={{ xs: 12, sm: 3 }}>
                                            <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>Village</Typography>
                                            <RefLocationInput
                                                label="Village"
                                                value={villageOptions.find((v) => String(v.villageId) === String(dealerForm.villageId))?.name || dealerForm.villageName || ''}
                                                selectedId={dealerForm.villageId}
                                                suggestions={
                                                    (dealerForm.mandalId
                                                        ? villageOptions.filter((v) => String(v.mandalId) === String(dealerForm.mandalId))
                                                        : villageOptions
                                                    ).map(v => ({ id: String(v.villageId), label: v.name }))
                                                }
                                                disabled={false}
                                                onTextChange={(text) => {
                                                    handleDealerFormChange('villageName', text);
                                                    // Normalize: lowercase, remove all spaces
                                                    const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                                                    const inputNorm = normalize(text);
                                                    const villageSuggs = dealerForm.mandalId
                                                        ? villageOptions.filter((v) => String(v.mandalId) === String(dealerForm.mandalId))
                                                        : villageOptions;
                                                    const villageMatch = villageSuggs.find(v => normalize(v.name) === inputNorm);
                                                    handleDealerFormChange('villageId', villageMatch ? String(villageMatch.villageId) : '');
                                                }}
                                                onSelect={(item) => {
                                                    handleDealerFormChange('villageId', item.id);
                                                    handleDealerFormChange('villageName', item.label);
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, pb: 3 }}>
                                    <Button
                                        variant="contained"
                                        sx={{ background: '#4F46E5', borderRadius: 3, fontWeight: 600, textTransform: 'none', px: 3, py: 1.2 }}
                                        onClick={handleDealerCreate}
                                        disabled={!(
                                            dealerForm.fullName &&
                                            (dealerForm.districtId || dealerForm.districtName) &&
                                            (dealerForm.mandalId || dealerForm.mandalName) &&
                                            (dealerForm.villageId || dealerForm.villageName)
                                        )}
                                    >
                                        Create Dealer
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        variant="contained"
                        sx={{
                            background: '#4F46E5',
                            borderRadius: 3,
                            boxShadow: '0px 4px 16px #5B5FEF1A',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 3,
                            py: 1.2,
                        }}
                        onClick={handleNext}
                    >
                        {/* ─── Show "Submit" in edit mode, "Next" in create mode ─── */}
                        {isEdit ? 'Submit' : 'Next'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reference Data Dialog - only used in CREATE mode */}
            {!isEdit && (
                <Dialog
                    open={openRefDialog}
                    onClose={() => {
                        formik.resetForm();
                        setOpenRefDialog(false);
                    }}
                    fullWidth
                    maxWidth="md"
                    slotProps={{ paper: { sx: { borderRadius: 3 } } }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, pb: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Reference Data
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={handleRefClose} aria-label="close dialog">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <DialogContent sx={{ pt: 2, pb: 1 }}>
                        {/* Reference input fields */}
                        <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                            <Grid item size={{ xs: 12, sm: 2 }}>
                                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>Name</Typography>
                                <TextField size="small" fullWidth name="newReferenceName" value={formik.values.newReferenceName || ''} onChange={e => {
                                    const value = e.target.value.replaceAll(/[^A-Za-z ]/g, '');
                                    formik.setFieldValue('newReferenceName', value);
                                }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 2.5 }}>
                                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>Mobile Number</Typography>
                                <TextField size="small" fullWidth name="newReferenceMobile" value={formik.values.newReferenceMobile || ''} onChange={e => {
                                    let value = e.target.value.replaceAll(/\D/g, '');
                                    if (value.length > 10) value = value.slice(0, 10);
                                    formik.setFieldValue('newReferenceMobile', value);
                                }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} slotProps={{ input: { maxLength: 10 } }} />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 2.5 }}>
                                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>District</Typography>
                                <Autocomplete
                                    options={districtOptions}
                                    value={districtOptions.find(d => String(d.districtId) === String(formik.values.newReferenceDistrictId)) || null}
                                    onChange={(_, newValue) => {
                                        formik.setFieldValue('newReferenceDistrictName', newValue?.name || '');
                                        formik.setFieldValue('newReferenceDistrictId', newValue ? String(newValue.districtId) : '');
                                        formik.setFieldValue('newReferenceMandalName', '');
                                        formik.setFieldValue('newReferenceMandalId', '');
                                        formik.setFieldValue('newReferenceVillageName', '');
                                        formik.setFieldValue('newReferenceVillageId', '');
                                    }}
                                    getOptionLabel={(d) => d ? d.name : ''}
                                    isOptionEqualToValue={(option, value) => String(option.districtId) === String(value.districtId)}
                                    clearOnEscape
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="Select district"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2.5,
                                                    backgroundColor: districtOptions.length === 1 ? '#F3F4F6' : '#FAFAFA',
                                                },
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#111827',
                                                    fontWeight: 400,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 2.5 }}>
                                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>Mandal</Typography>
                                <RefLocationInput
                                    label="Mandal"
                                    value={formik.values.newReferenceMandalName}
                                    selectedId={formik.values.newReferenceMandalId}
                                    suggestions={(
                                        formik.values.newReferenceDistrictId
                                            ? mandalOptions.filter(m => String(m.districtId) === String(formik.values.newReferenceDistrictId))
                                            : mandalOptions
                                    ).map(m => ({ id: String(m.mandalId), label: m.name }))}
                                    disabled={false}
                                    onTextChange={(text) => {
                                        formik.setFieldValue('newReferenceMandalName', text);
                                        // Normalize: lowercase, remove all spaces
                                        const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                                        const inputNorm = normalize(text);
                                        const match = (formik.values.newReferenceDistrictId
                                            ? mandalOptions.filter(m => String(m.districtId) === String(formik.values.newReferenceDistrictId))
                                            : mandalOptions
                                        ).find(m => normalize(m.name) === inputNorm);
                                        formik.setFieldValue('newReferenceMandalId', match ? String(match.mandalId) : '');
                                        formik.setFieldValue('newReferenceVillageName', '');
                                        formik.setFieldValue('newReferenceVillageId', '');
                                    }}
                                    onSelect={(item) => {
                                        formik.setFieldValue('newReferenceMandalName', item.label);
                                        formik.setFieldValue('newReferenceMandalId', item.id);
                                        formik.setFieldValue('newReferenceVillageName', '');
                                        formik.setFieldValue('newReferenceVillageId', '');
                                    }}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 2.5 }}>
                                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, mb: 0.5 }}>Village</Typography>
                                <RefLocationInput
                                    label="Village"
                                    value={formik.values.newReferenceVillageName}
                                    selectedId={formik.values.newReferenceVillageId}
                                    suggestions={(
                                        formik.values.newReferenceMandalId
                                            ? villageOptions.filter(v => String(v.mandalId) === String(formik.values.newReferenceMandalId))
                                            : villageOptions
                                    ).map(v => ({ id: String(v.villageId), label: v.name }))}
                                    disabled={false}
                                    onTextChange={(text) => {
                                        formik.setFieldValue('newReferenceVillageName', text);
                                        // Normalize: lowercase, remove all spaces
                                        const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                                        const inputNorm = normalize(text);
                                        const match = (formik.values.newReferenceMandalId
                                            ? villageOptions.filter(v => String(v.mandalId) === String(formik.values.newReferenceMandalId))
                                            : villageOptions
                                        ).find(v => normalize(v.name) === inputNorm);
                                        formik.setFieldValue('newReferenceVillageId', match ? String(match.villageId) : '');
                                    }}
                                    onSelect={(item) => {
                                        formik.setFieldValue('newReferenceVillageName', item.label);
                                        formik.setFieldValue('newReferenceVillageId', item.id);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={1} md={1} sx={{ mt: { xs: 3, sm: 0 }, display: 'flex', alignItems: 'end' }}>
                                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                                    <IconButton color="primary" onClick={() => {
                                        if (formik.values.newReferenceName && formik.values.newReferenceMobile && formik.values.newReferenceDistrictName && formik.values.newReferenceMandalName && formik.values.newReferenceVillageName) {
                                            const newRefs = [
                                                ...formik.values.references,
                                                {
                                                    name: formik.values.newReferenceName,
                                                    mobile: formik.values.newReferenceMobile,
                                                    districtId: formik.values.newReferenceDistrictId,
                                                    districtName: formik.values.newReferenceDistrictName,
                                                    mandalId: formik.values.newReferenceMandalId,
                                                    mandalName: formik.values.newReferenceMandalName,
                                                    villageId: formik.values.newReferenceVillageId,
                                                    villageName: formik.values.newReferenceVillageName,
                                                    referenceFarmerId: formik.values.newReferenceFarmerId || undefined
                                                }
                                            ];
                                            formik.setFieldValue('references', newRefs);
                                            formik.setFieldValue('newReferenceName', '');
                                            formik.setFieldValue('newReferenceMobile', '');
                                            formik.setFieldValue('newReferenceDistrictName', '');
                                            formik.setFieldValue('newReferenceDistrictId', '');
                                            formik.setFieldValue('newReferenceMandalName', '');
                                            formik.setFieldValue('newReferenceMandalId', '');
                                            formik.setFieldValue('newReferenceVillageName', '');
                                            formik.setFieldValue('newReferenceVillageId', '');
                                            // Remove error if now >= 2 valid references
                                            const validCount = newRefs.filter(ref => ref.name && ref.mobile && ref.districtId && (ref.mandalId || ref.mandalName) && (ref.villageId || ref.villageName)).length;
                                            if (validCount >= 2) setRefSubmitError('');
                                        }
                                    }} sx={{ bgcolor: '#EEF4FF', borderRadius: 2 }}>
                                        <AddIcon /> Add
                                    </IconButton>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Table of added references */}
                        {formik.values.references && formik.values.references.filter(ref => ref.name || ref.mobile || ref.villageName || ref.districtName || ref.mandalName).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Reference List</Typography>
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    {formik.values.references.filter(ref => ref.name || ref.mobile || ref.villageName || ref.districtName || ref.mandalName).map((ref, idx) => (
                                        <Paper key={ref.id} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 1 }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Name:<span style={{ marginLeft: 6, fontWeight: 400 }}>{ref.name}</span></Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Mobile:<span style={{ marginLeft: 6, fontWeight: 400 }}>{ref.mobile}</span></Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>District:<span style={{ marginLeft: 6, fontWeight: 400 }}>{ref.districtName || districtOptions.find(d => String(d.districtId) === String(ref.district))?.name || ''}</span></Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Mandal:<span style={{ marginLeft: 6, fontWeight: 400 }}>{ref.mandalName || mandalOptions.find(m => String(m.mandalId) === String(ref.mandal))?.name || ''}</span></Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Village:<span style={{ marginLeft: 6, fontWeight: 400 }}>{ref.villageName || villageOptions.find(v => String(v.villageId) === String(ref.village))?.name || ''}</span></Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                                    <IconButton aria-label="delete reference" color="error" onClick={() => {
                                                        const arr = [...formik.values.references];
                                                        arr.splice(idx, 1);
                                                        formik.setFieldValue('references', arr);
                                                    }}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f3f6f1' }}>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Mobile</TableCell>
                                                    <TableCell>District</TableCell>
                                                    <TableCell>Mandal</TableCell>
                                                    <TableCell>Village</TableCell>
                                                    <TableCell align="center">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {formik.values.references.filter(ref => ref.name || ref.mobile || ref.villageName || ref.districtName || ref.mandalName).map((ref, idx) => (
                                                    <TableRow key={ref.id}>
                                                        <TableCell>{ref.name}</TableCell>
                                                        <TableCell>{ref.mobile}</TableCell>
                                                        <TableCell>{ref.districtName || districtOptions.find(d => String(d.districtId) === String(ref.district))?.name || ''}</TableCell>
                                                        <TableCell>{ref.mandalName || mandalOptions.find(m => String(m.mandalId) === String(ref.mandal))?.name || ''}</TableCell>
                                                        <TableCell>{ref.villageName || villageOptions.find(v => String(v.villageId) === String(ref.village))?.name || ''}</TableCell>
                                                        <TableCell align="center">
                                                            <IconButton aria-label="delete reference" color="error" onClick={() => {
                                                                const arr = [...formik.values.references];
                                                                arr.splice(idx, 1);
                                                                formik.setFieldValue('references', arr);
                                                                // Remove error if now >= 2 valid references
                                                                const validCount = arr.filter(ref => ref.name && ref.mobile && ref.districtId && (ref.mandalId || ref.mandalName) && (ref.villageId || ref.villageName)).length;
                                                                if (validCount >= 2) setRefSubmitError('');
                                                            }}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Button
                            variant="contained"
                            sx={{
                                background: '#4F46E5',
                                borderRadius: 3,
                                boxShadow: '0px 4px 16px #5B5FEF1A',
                                fontWeight: 600,
                                textTransform: 'none',
                                px: 3,
                                py: 1.2,
                            }}
                            onClick={() => {
                                const refCount = (formik.values.references || []).filter(ref => ref.name && ref.mobile && ref.districtId && (ref.mandalId || ref.mandalName) && (ref.villageId || ref.villageName)).length;
                                if (refCount < 2) {
                                    setRefSubmitError('Please add at least 2 valid references before submitting.');
                                    return;
                                }
                                setRefSubmitError('');
                                formik.handleSubmit();
                            }}
                        >
                            Submit Data
                        </Button>
                        {refSubmitError && (
                            <Typography color="error" sx={{ mt: 1, fontSize: 14 }}>{refSubmitError}</Typography>
                        )}
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

export default FarmerCommitmentDialogs;

FarmerCommitmentDialogs.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedCommitmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    farmerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    seasonOptions: PropTypes.array,
    farmerOptions: PropTypes.array,
    cropOptions: PropTypes.array,
    varietyOptions: PropTypes.array,
    dealerOptions: PropTypes.array,
};

FarmerCommitmentDialogs.defaultProps = {
    open: false,
    onClose: () => {},
    selectedCommitmentId: null,
    farmerId: null,
    seasonOptions: [],
    farmerOptions: [],
    cropOptions: [],
    varietyOptions: [],
    dealerOptions: [],
};